import React from "react";
import {
  ShieldCheck,
  Truck,
  Briefcase,
  ArrowRight,
  MapPin,
  Wallet,
  Receipt,
  Fuel,
  Route,
  PieChart,
  ClipboardList,
  Calculator,
  Activity,
  ScanLine,
  ShoppingBasket,
  Plane,
  RefreshCcw,
  List,
} from "lucide-react";

/**
 * PillarsSection — "Three lenses, one Nerve Center."
 * A premium, additive showcase card grid highlighting who SpendNova is built for.
 * The existing Military / SSG O'Hara Mission Stories section is untouched.
 */

const Pillar = ({
  tone,
  icon: Icon,
  personaTag,
  personaName,
  headline,
  description,
  workFeaturesLabel,
  features,
  ctaHref,
  ctaLabel,
  testId,
}) => {
  const palette = {
    green: {
      accent: "#22C585",
      bg: "linear-gradient(160deg, rgba(34,197,133,0.16) 0%, rgba(34,197,133,0.04) 100%)",
      border: "rgba(34,197,133,0.42)",
      chip: "rgba(34,197,133,0.16)",
      chipBorder: "rgba(34,197,133,0.45)",
    },
    amber: {
      accent: "#F4C752",
      bg: "linear-gradient(160deg, rgba(244,199,82,0.14) 0%, rgba(244,199,82,0.03) 100%)",
      border: "rgba(244,199,82,0.42)",
      chip: "rgba(244,199,82,0.16)",
      chipBorder: "rgba(244,199,82,0.45)",
    },
    purple: {
      accent: "#B7A5FF",
      bg: "linear-gradient(160deg, rgba(183,165,255,0.14) 0%, rgba(183,165,255,0.03) 100%)",
      border: "rgba(183,165,255,0.42)",
      chip: "rgba(183,165,255,0.16)",
      chipBorder: "rgba(183,165,255,0.45)",
    },
  }[tone];

  return (
    <article
      className="relative rounded-3xl p-6 md:p-7 overflow-hidden h-full flex flex-col transition-transform duration-200 hover:-translate-y-[2px]"
      data-testid={testId}
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow:
          "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl"
        style={{ background: palette.accent }}
      />
      <div className="relative flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: palette.chip,
            border: `1px solid ${palette.chipBorder}`,
            boxShadow: `0 20px 40px -20px ${palette.accent}55`,
          }}
        >
          <Icon size={22} style={{ color: palette.accent }} />
        </div>
        <div className="min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.22em] font-bold"
            style={{ color: palette.accent }}
          >
            {personaTag}
          </div>
          <div
            className="text-lg font-extrabold text-white leading-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            {personaName}
          </div>
        </div>
      </div>

      <h3
        className="relative mt-5 text-xl md:text-[22px] font-extrabold text-white leading-tight"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        {headline}
      </h3>
      <p className="relative mt-2 text-[13.5px] text-[#C7D0DF] leading-relaxed">
        {description}
      </p>

      <ul className="relative mt-5 space-y-2.5">
        <li
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold pb-1"
          style={{ color: palette.accent }}
          data-testid={`${testId}-work-features-label`}
        >
          <span
            className="w-1 h-1 rounded-full"
            style={{ background: palette.accent }}
          />
          Work Features · {workFeaturesLabel}
        </li>
        {features.map((f) => (
          <li key={f.label} className="flex items-start gap-2.5">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{
                background: palette.chip,
                border: `1px solid ${palette.chipBorder}`,
              }}
            >
              <f.icon size={13} style={{ color: palette.accent }} />
            </div>
            <div className="min-w-0">
              <div
                className="text-[13.5px] text-white font-semibold leading-snug"
                style={{ fontFamily: "Sora, Inter, sans-serif" }}
              >
                {f.label}
              </div>
              <div className="text-[12px] text-[#C7D0DF] leading-snug">
                {f.body}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {ctaHref && (
        <a
          href={ctaHref}
          className="relative mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-bold self-start px-3 py-1.5 rounded-lg transition-colors"
          style={{
            color: palette.accent,
            background: palette.chip,
            border: `1px solid ${palette.chipBorder}`,
          }}
          data-testid={`${testId}-cta`}
        >
          {ctaLabel || "See in action"}
          <ArrowRight size={13} />
        </a>
      )}
    </article>
  );
};

const PillarsSection = () => (
  <section
    id="pillars"
    className="relative py-14 md:py-20"
    data-testid="section-pillars"
  >
    <div className="mx-auto max-w-[1200px] px-4 md:px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#22C585]">
          Built for every financial journey
        </div>
        <h2
          className="mt-3 text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
          data-testid="pillars-title"
        >
          Three lenses. One Elite Foundation.
        </h2>
        <p className="mt-4 text-[15px] md:text-base text-[#C7D0DF] leading-relaxed">
          Same Bunker Security core. Same Safe-to-Spend engine. Three tailored
          lenses — so it feels built for you, whether you're on a base, on a
          highway, or on the clock.
        </p>
      </div>

      {/* UNIVERSAL CORE — shared across every lens */}
      <div
        className="relative mt-10 rounded-3xl p-6 md:p-7 overflow-hidden"
        data-testid="universal-core"
        style={{
          background:
            "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow:
            "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
            The Elite Foundation
          </div>
          <div className="text-[13px] text-[#C7D0DF]">
            Every lens includes the same universal core.
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Activity, k: "The Nova Life Feed", d: "Real-time financial suggestions at the bottom of every screen." },
            { icon: ScanLine, k: "The Receipt Scanner", d: "Digital basket for scanning business receipts, gas sheets, and invoices." },
            { icon: ShoppingBasket, k: "Smart Shopping Lists", d: "Automated lists that sync with the scanner to organize journey essentials." },
            { icon: Plane, k: "The Journey Planner", d: "For vacations or work trips — plan costs before you go." },
            { icon: RefreshCcw, k: "Subscription Manager", d: "A dedicated view to see every recurring payment in one place." },
            { icon: List, k: "Transaction Command", d: "A clear list of every dollar in and out." },
          ].map((c) => (
            <div
              key={c.k}
              className="rounded-xl p-3 flex items-start gap-3"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
              data-testid={`core-${c.k.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: "rgba(34,197,133,0.14)",
                  border: "1px solid rgba(34,197,133,0.35)",
                }}
              >
                <c.icon size={14} className="text-[#22C585]" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-[13.5px] text-white font-semibold leading-snug"
                  style={{ fontFamily: "Sora, Inter, sans-serif" }}
                >
                  {c.k}
                </div>
                <div className="text-[12px] text-[#C7D0DF] leading-snug mt-0.5">
                  {c.d}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 grid gap-6 md:gap-7 lg:grid-cols-3 items-stretch">
        {/* PILLAR 1 — THE BUNKER (Military) */}
        <Pillar
          tone="green"
          icon={ShieldCheck}
          personaTag="Military"
          personaName="The Bunker"
          headline="Built for PCS lifecycles and TDY journeys."
          description="For service members and their families. Purpose-built for PCS lifecycles, TDY per-diem, BAH, and the receipts every voucher demands."
          workFeaturesLabel="Military Mission"
          features={[
            {
              icon: MapPin,
              label: "PCS Command Center",
              body: "JTR-informed routing, 8-hour driving increments, lodging planning.",
            },
            {
              icon: Receipt,
              label: "Voucher Preparation Vault",
              body: "Every hotel folio & meal receipt drops into a Voucher Preparation Worksheet.",
            },
            {
              icon: Wallet,
              label: "BAH & duty-station budgeting",
              body: "Housing, schools, hospitals — mapped before the moving truck rolls.",
            },
          ]}
          ctaHref="#mission-stories"
          ctaLabel="See PFC Alex & SSG O'Hara"
          testId="pillar-military"
        />

        {/* PILLAR 2 — THE LOG (Trucker) */}
        <Pillar
          tone="amber"
          icon={Truck}
          personaTag="Trucker"
          personaName="The Log"
          headline="Truck-safe. Diesel-smart. IFTA-ready."
          description="For owner-operators and OTR drivers. Route the truck safely, buy fuel where it's cheapest, and file the paperwork without losing a Sunday."
          workFeaturesLabel="Trucker Route-O-Meter"
          features={[
            {
              icon: Route,
              label: "Truck-safe routing (ORS)",
              body: "HGV-aware routing that respects height, weight, length, and hazmat constraints.",
            },
            {
              icon: Fuel,
              label: "Diesel price planning (EIA)",
              body: "Live per-gallon intel across the corridor — buy where the margins hide.",
            },
            {
              icon: Calculator,
              label: "IFTA fuel-tax reporting",
              body: "Miles by jurisdiction, gallons by state, quarterly report exported clean.",
            },
          ]}
          ctaHref="#reality-stories"
          ctaLabel="See The Long Haul"
          testId="pillar-trucker"
        />

        {/* PILLAR 3 — THE BALANCE (Freelancer) */}
        <Pillar
          tone="purple"
          icon={Briefcase}
          personaTag="Freelancer"
          personaName="The Balance"
          headline="Every invoice tracked. Every quarter shielded."
          description="For creatives, consultants, and independent operators. Categorize expenses, stash quarterly tax reserves, and export a CRM-ready ledger."
          workFeaturesLabel="Freelancer Tax Advisor"
          features={[
            {
              icon: PieChart,
              label: "Expense categorizing",
              body: "Automatic Schedule-C bucketing across gear, travel, software, and meals.",
            },
            {
              icon: ShieldCheck,
              label: "Proactive tax planning",
              body: "Auto-stash 25–35% of every invoice into a segregated quarterly tax reserve.",
            },
            {
              icon: ClipboardList,
              label: "CRM-ready ledgers",
              body: "Export client-by-client ledgers to QuickBooks, Wave, or HoneyBook — 1099-clean.",
            },
          ]}
          ctaHref="#reality-stories"
          ctaLabel="See The Freelance Shield"
          testId="pillar-freelancer"
        />
      </div>
    </div>
  </section>
);

export default PillarsSection;
